import { doc, collection, query, where, onSnapshot, updateDoc } from 'firebase/firestore';
import { firestore } from '../../../context/FirebaseConfig';
import { planActiveDocumentClosures } from '../utils/activeDocumentReconciliation';
import {
  PRODUCTION_RUNS_COLLECTION_PATH,
  PRODUCTION_EVENTS_COLLECTION_PATH,
  RECIPE_INGREDIENTS_COLLECTION_PATH,
} from '../../../shared_components/utils/FirebaseUtils';

/**
 * Custom hook to manage Firestore subscriptions
 * Handles subscribing to various collections and documents
 */
export function useFirestoreSubscriptions(
  currentUser,
  setButtonSelection,
  setShiftInfo,
  setNRows,
  setNColumns,
  setLinkedDevices,
  setDeviceName,
  setSettings,
  setActiveDocument,
  setConveyorCode,
  setProductionRuns,
  setIngredients,
  updateUiMode, // Receive updateUiMode from ThemeContext
) {
  // Function to subscribe to button selection updates
  function subscribeToButtonSelection() {
    /* Subscribe to the button selection in the firestore */
    if (!currentUser?.company?.company || !currentUser?.device_id) {
      return () => {};
    }

    const q = doc(firestore, 'clients', currentUser.company.company, 'devices', currentUser.device_id);

    return onSnapshot(q, async (snapshot) => {
      const document = snapshot.data();
      if (document?.last_plant_feed_selection?.button_selection) {
        setButtonSelection(document.last_plant_feed_selection.button_selection);
      }
      if (document?.lastPlantFeedShiftInfo) {
        setShiftInfo(document.lastPlantFeedShiftInfo);
      }
      if (document?.last_plant_feed_selection?.n_rows && document?.last_plant_feed_selection?.n_columns) {
        setNRows(document.last_plant_feed_selection.n_rows);
        setNColumns(document.last_plant_feed_selection.n_columns);
      }
      if (document?.last_plant_feed_selection?.linked_devices) {
        setLinkedDevices(document.last_plant_feed_selection.linked_devices);
      }
      setDeviceName(document?.display_name ?? currentUser.device_id);

      // Also set the conveyor code if it exists, otherwise use "BAND" as fallback
      if (document?.conveyor_code) {
        setConveyorCode(document.conveyor_code);
      } else {
        setConveyorCode('BAND');

        // Update the Firestore document with the default conveyor_code
        try {
          await updateDoc(q, { conveyor_code: 'BAND' });
        } catch (error) {
          console.error('Error updating device with default conveyor_code:', error);
        }
      }
    });
  }

  function subscribeToActiveDocuments(closeActiveDocumentsByIds) {
    /* Subscribe to active documents (i.e. clicked buttons) in the firestore */
    if (!currentUser?.company?.company || !currentUser?.device_id) {
      return () => {};
    }

    const q = query(
      collection(
        firestore,
        'clients',
        currentUser.company.company,
        'devices',
        currentUser.device_id,
        'bale_feed_timestamps',
      ),
      where('active', '==', true),
    );

    // Debounce only the *displayed* active document so rapid, server-confirmed
    // transitions (offline replay catching up, the merge closing duplicates) coalesce
    // into the final settled state instead of flashing intermediate stamps. The
    // reconciliation writes below are NOT debounced — they fire promptly to heal the
    // data. The user's own in-flight write (hasPendingWrites === true) is applied
    // immediately so button presses still feel instant.
    const REPLAY_SETTLE_MS = 250;
    let displayTimer = null;

    const commitActiveDocument = (next, immediate) => {
      if (displayTimer) {
        clearTimeout(displayTimer);
        displayTimer = null;
      }
      if (immediate) {
        setActiveDocument(next);
      } else {
        displayTimer = setTimeout(() => {
          displayTimer = null;
          setActiveDocument(next);
        }, REPLAY_SETTLE_MS);
      }
    };

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const activeDocuments = {};
      snapshot.forEach((document) => {
        activeDocuments[document.id] = document.data();
      });

      // Local optimistic writes reflect instantly; remote/replay churn is debounced.
      const immediate = snapshot.metadata.hasPendingWrites;

      if (Object.keys(activeDocuments).length === 0) {
        // No active documents
        commitActiveDocument(false, immediate);
      } else if (Object.keys(activeDocuments).length === 1) {
        // Single active document
        commitActiveDocument(
          {
            id: Object.keys(activeDocuments)[0],
            data: Object.values(activeDocuments)[0],
          },
          immediate,
        );
      } else {
        // Multiple active documents -> Keep newest, close others
        const { newest, closures } = planActiveDocumentClosures(activeDocuments);
        console.warn(`Multiple active documents: keeping ${newest.id}, closing ${closures.length}`);
        commitActiveDocument(newest, immediate);

        // Only repair on server-confirmed snapshots. A cached view can contain
        // "ghost" documents that don't exist on the server (e.g. an offline create
        // whose batch was rejected on replay). Closing a ghost is rejected with
        // not-found, the SDK reverts the write in the cache, the listener re-fires
        // with the same cached view, and the client loops forever.
        if (snapshot.metadata.fromCache) {
          return;
        }

        // Close all others with end times derived from the superseding stamp's
        // start_timestamp (not this device's clock), so closing stamps another
        // device replayed after being offline cannot corrupt their end times.
        if (closeActiveDocumentsByIds && closures.length > 0) {
          closeActiveDocumentsByIds(currentUser.device_id, closures);
        } else if (!closeActiveDocumentsByIds) {
          console.error('closeActiveDocumentsByIds function not provided');
        }
      }
    });

    return () => {
      if (displayTimer) {
        clearTimeout(displayTimer);
      }
      unsubscribe();
    };
  }

  function subscribeToBaleFeedSettings() {
    /* Subscribe to document containing settings such as bale-feed-workers names */

    // Check if currentUser and company are available
    if (!currentUser || !currentUser.company || !currentUser.company.company) {
      return () => {};
    }

    const q = doc(firestore, 'clients', currentUser.company.company, 'client_settings', 'bale_feed_settings');

    return onSnapshot(
      q,
      async (snapshot) => {
        if (!snapshot.exists()) {
          if (snapshot.metadata.fromCache) return;
          if (updateUiMode) {
            await updateUiMode('dark-mode');
          }
          return;
        }

        const data = snapshot.data();
        setSettings(data);

        const appMode = data?.stamp_app_mode;

        if (appMode === 'light-mode' || appMode === 'dark-mode') {
          if (updateUiMode) {
            updateUiMode(appMode, false);
          }
        } else {
          if (snapshot.metadata.fromCache) return;
          if (updateUiMode) {
            await updateUiMode('dark-mode');
          }
        }
      },
      (error) => {
        console.error('Error subscribing to bale feed settings:', error);
      },
    );
  }

  function subscribeToProductions() {
    if (!currentUser?.company?.company || !currentUser?.device_id) return () => {};

    const now = new Date();
    const twoDaysAgo = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000);
    const twoDaysInFuture = new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000);

    return onSnapshot(
      query(
        collection(firestore, PRODUCTION_RUNS_COLLECTION_PATH(currentUser.company.company)),
        where('devices', 'array-contains', currentUser.device_id),
        where('start_date', '<=', twoDaysInFuture),
        where('end_date', '>=', twoDaysAgo),
      ),
      (snapshot) => {
        setProductionRuns(
          snapshot.docs
            .map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                ...data,
                start_date: data.start_date?.toDate(),
                end_date: data.end_date?.toDate(),
                created_at: data.created_at?.toDate(),
                updated_at: data.updated_at?.toDate(),
              };
            })
            .filter((run) => run.draft !== true),
        );
      },
      (error) => console.error('Error subscribing to production runs:', error),
    );
  }

  /**
   * Subscribes to all production events for the currently running production.
   * A single Firestore listener fetches all event types (material_correction,
   * time_correction, production_instruction) scoped to the current production_id.
   *
   * When no production is running, productionEvents is cleared immediately.
   * The subscription is re-created whenever the active production changes.
   *
   * Downstream consumers filter the flat events array by type and device_id as needed.
   *
   * @param {Object|null} currentRunningProduction - Currently active production run.
   * @param {Function} setProductionEvents - React state setter for the events array.
   * @returns {Function} Cleanup function that tears down the listener.
   */
  function subscribeToProductionEvents(currentRunningProduction, setProductionEvents) {
    if (!currentUser?.company?.company) return () => {};

    if (!currentRunningProduction?.id) {
      setProductionEvents([]);
      return () => {};
    }

    const eventsRef = collection(firestore, PRODUCTION_EVENTS_COLLECTION_PATH(currentUser.company.company));

    // Converts a Firestore document to a plain JS object with Timestamps pre-converted to JS Dates.
    function toEventRecord(doc) {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        created_at: data.created_at?.toDate?.(),
        executed_at: data.executed_at?.toDate?.(),
        confirmed_timestamp: data.confirmed_timestamp?.toDate?.(),
        previous_start_date: data.previous_start_date?.toDate?.(),
        new_start_date: data.new_start_date?.toDate?.(),
        previous_end_date: data.previous_end_date?.toDate?.(),
        new_end_date: data.new_end_date?.toDate?.(),
      };
    }

    return onSnapshot(
      query(eventsRef, where('production_id', '==', currentRunningProduction.id)),
      (snapshot) => setProductionEvents(snapshot.docs.map(toEventRecord)),
      (error) => console.error('Error subscribing to production events:', error),
    );
  }

  function subscribeToIngredients() {
    // Check if currentUser and company are available
    if (!currentUser || !currentUser.company || !currentUser.company.company) {
      return () => {};
    }

    const ingredientsRef = collection(firestore, RECIPE_INGREDIENTS_COLLECTION_PATH(currentUser.company.company));
    const q = query(ingredientsRef);

    return onSnapshot(q, (snapshot) => {
      const ingredientsData = {};
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        ingredientsData[doc.id] = {
          id: doc.id,
          ...data,
          created_at: data.created_at?.toDate?.(),
          updated_at: data.updated_at?.toDate?.(),
        };
      });
      setIngredients(ingredientsData);
    });
  }

  return {
    subscribeToButtonSelection,
    subscribeToBaleFeedSettings,
    subscribeToActiveDocuments,
    subscribeToProductions,
    subscribeToProductionEvents,
    subscribeToIngredients,
  };
}
